import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import { publish } from '@stubhub/pubsub-utils';
import DateFilter from '@stubhub/react-date-filter';
import PillBar from '@stubhub/react-pill-bar';
import { connect } from '@stubhub/react-store-provider';

import withHeader from '../../common/higherOrderComponents/withHeader';
import { container as EventListPanelContainer } from '../../modules/react-event-list-panel';
import PerformerSuggestions from '../../modules/react-performer-suggestions/PerformerSuggestions';
import RequestEventComponent from '../../modules/react-request-events';
import SRPBrandDrivers from '../../modules/react-search-results-brand-drivers';
import { SELL_HUB_EVENTS } from '../../tracking/track-enum';

import { SearchResultsMessages, TABS } from './config';
import controller from './controller';

import './index.scss';

@connect(controller)
@withHeader
export class SearchResults extends Component {
  constructor(props, context) {
    super(props, context);
    const { type = null } = props.query;
    props.getDefaultReducer();
    const { globalRegistry } = context || {};
    this.disableMultipleEventsSwitch =
      (globalRegistry &&
        globalRegistry.getFeatureValue('sellhub.multipleEventsSwitch.hide')) ||
      false;
    this.multiEventUrl = '/sell/sth/mevent';

    this.state = {
      isParkingSelected: false,
      isMultipleEventToggleSelected: false,
      selectedEvents: {},
      numSelectedEvents: 0,
      queryType: type,
      tabClicked: false,
    };
  }

  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  static propTypes = {
    query: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
  };

  onToggleMultiEvent = () => {
    this.setState((prevState) => ({
      isMultipleEventToggleSelected: !prevState.isMultipleEventToggleSelected,
      numSelectedEvents: 0,
      selectedEvents: {},
    }));
  };

  onTabClicked = (tab) => {
    this.setState((prevState) => ({
      searchParam: {
        ...prevState.searchParam,
        eventType: tab === TABS[1] ? 'parking' : 'main',
      },
      isParkingSelected: tab === TABS[1],
      tabClicked: true,
    }));
  };

  getPillBarLayout = (tabParams) => {
    const tabsControl = { itemType: 'tabs', tabParams };
    const layout = {
      leftSection: tabParams.orientation === 'left' ? [tabsControl] : [],
      rightSection: tabParams.orientation === 'right' ? [tabsControl] : [],
    };

    return layout;
  };

  updateTitle = (title) => {
    this.setState({ title });
  };

  getTabsConfig = (tabs) => {
    const tabsConfig = {};
    SearchResultsMessages.forEach((message, index) => {
      tabsConfig[`${tabs[index]}`] = {
        displayName: <FormattedMessage {...message[`${tabs[index]}`]} />,
      };
    });

    return tabsConfig;
  };

  navigateToMultiListingFlow = () => {
    const { numSelectedEvents, selectedEvents } = this.state;
    const selected = Object.keys(selectedEvents).filter((event) => {
      return selectedEvents[event] === true;
    });
    window.location = numSelectedEvents
      ? `${this.multiEventUrl}?eventIds=${selected.join(',')}`
      : '#';
  };

  applyFooterCSS() {
    if (__CLIENT__ && document) {
      document.body.classList.add('global-footer-add'); // Add margin in footer css when multi-event footer is shown.
    }
  }

  discardFooterCSS() {
    if (__CLIENT__ && document) {
      document.body.classList.remove('global-footer-add');
    }
  }

  componentDidMount() {
    if (document) {
      publish(SELL_HUB_EVENTS.SEARCH_RESULTS);
    }
  }

  componentWillUnmount() {
    this.discardFooterCSS();
  }

  dataLayerReducer = (state) => {
    const { events, numFound } = state['app.sell.search.events.all'];
    const { isParkingSelected } = this.state;
    const { query = {}, history = {} } = this.props;
    const {
      location: { pathname, search },
    } = history;

    return {
      completeUrl: `${pathname}${search}`,
      historySize: history.length,
      eventType: isParkingSelected ? 'Parking' : 'Events',
      queryParams: query,
      listings: events,
      numFound,
    };
  };

  handleUpdateSellEvents = (selectedEvents) => {
    const numSelectedEvents = Object.keys(selectedEvents).filter((event) => {
      return selectedEvents[event] === true;
    }).length;
    this.setState({ selectedEvents, numSelectedEvents });
  };

  render() {
    const {
      isParkingSelected,
      isMultipleEventToggleSelected,
      numSelectedEvents,
      selectedEvents,
      queryType,
    } = this.state;
    const { query = {}, shstoreId } = this.props;
    const windowWidth = __CLIENT__ ? window.innerWidth : 600;
    const isSmall = windowWidth < 640;
    const url = (__CLIENT__ && window?.location.href) || '';
    const renderParkingEvents =
      url.toUpperCase().indexOf('PARKING') > -1 && !this.state.tabClicked;

    /** ---- Events/Parking filter Pill Bar layout */
    const parkingFilterProps = {
      tabNames: TABS,
      tabsConfig: this.getTabsConfig(TABS),
      cojoinedTabs: true,
      selectedTabName:
        this.state.isParkingSelected || renderParkingEvents ? TABS[1] : TABS[0],
      onTabClickHandler: this.onTabClicked,
    };

    /** ---- List tickets pill bar layout */
    const listTicketsDisplayName =
      numSelectedEvents === 0 ? (
        <FormattedMessage
          id="SearchResults.PillBar.MultiListingBtn.NoEventsSelected"
          defaultMessage="List Tickets"
        />
      ) : (
        <FormattedMessage
          id="SearchResults.PillBar.MultiListingBtn.EventsSelected"
          defaultMessage="List {numSelectedEvents,number} Tickets"
          values={{ numSelectedEvents }}
        />
      );

    const multiListingBtnProps = {
      tabNames: ['MultiListingBtn'],
      tabsConfig: { MultiListingBtn: { displayName: listTicketsDisplayName } },
      cojoinedTabs: false,
      onTabClickHandler: this.navigateToMultiListingFlow,
      disableTheme: !numSelectedEvents,
      selectedTabName: numSelectedEvents ? 'MultiListingBtn' : '',
    };

    // Logic to show PerformerSuggestions module, if they are navigated to a searchEventID, it should not display
    const showPerformerPills =
      __CLIENT__ && !window?.location.href.toString().includes('searchEvent');
    const getSearchParam = {
      q: query.q,
      eventType: isParkingSelected || renderParkingEvents ? 'parking' : 'main',
    };
    isMultipleEventToggleSelected
      ? this.applyFooterCSS()
      : this.discardFooterCSS();

    return (
      <div className="SearchResults">
        <PerformerSuggestions
          showPerformerPills={showPerformerPills}
          id={query.q}
          queryType={query.type}
          updatePerformerSuggestions={this.props.getPerformerSuggestions}
          performerSuggestions={this.props.performerSuggestions}
        />
        <div className="SellHub">
          <div className="SearchResults__SearchContainer">
            <div className="Search__Main">
              <div className="Search__Header">
                {this.props.isParkingEnabled && (
                  <PillBar
                    {...this.getPillBarLayout({
                      ...parkingFilterProps,
                      orientation: 'left',
                    })}
                    className="PillBar__Sell"
                    isSell
                  />
                )}
                <DateFilter
                  className="Sell__DateFilter"
                  selected="all-dates"
                  inPillBar
                  useAltMsgs
                  draggable={isSmall}
                  useBackdrop={isSmall}
                  isSell
                />
              </div>
              <div className="Search__Content">
                <EventListPanelContainer
                  isMultipleEventToggleSelected={isMultipleEventToggleSelected}
                  onToggleClicked={this.onToggleMultiEvent}
                  parkingFilter={
                    isParkingSelected || renderParkingEvents ? TABS[1] : null
                  }
                  updateSellEvents={this.handleUpdateSellEvents}
                  selectedEvents={selectedEvents}
                  maxSelectedEvents={10}
                  queryType={queryType}
                  updateTitle={this.updateTitle}
                  disableMultipleEventsSwitch={this.disableMultipleEventsSwitch}
                  disableGeoLocation
                  isSell
                  namespace="app.sell.search.events.all"
                  numSelectedEvents={numSelectedEvents}
                  id={query.q}
                  spellCheck
                  searchParams={getSearchParam}
                />
              </div>
            </div>
            <div className="Search__SideBar">
              <SRPBrandDrivers />
              <RequestEventComponent
                storeId={shstoreId}
                className={isSmall ? 'hidden' : ''}
                showImage={false}
              />
              <RequestEventComponent
                storeId={shstoreId}
                className={isSmall ? '' : 'hidden'}
              />
            </div>
          </div>
          <div
            className={`Search__Footer ${
              isMultipleEventToggleSelected ? '' : 'hidden'
            }`}
          >
            <div className="Search__Footer--Content">
              <div className="Search__Footer--Message">
                <FormattedMessage
                  id="SearchResults.Footer.SameSeatsVenue"
                  defaultMessage="Choose Ticket Type"
                />
              </div>
              <PillBar
                {...this.getPillBarLayout({
                  ...multiListingBtnProps,
                  orientation: 'left',
                })}
                className="PillBar__Sell PillBar__MultiListingBtn"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectIntl(SearchResults);
